import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Stack,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from '../../components/Iconify';

// ----------------------------------------------------------------------

export default function CoreTimeChangeDialog({
  isOpen,
  closeDialog,
  onConfirm,
  coreTime,
  submitting = false,
}) {
  const [reason, setReason] = useState('');

  const handleClose = () => {
    setReason('');
    closeDialog();
  };

  const handleConfirm = () => {
    if (reason.trim()) {
      onConfirm(coreTime, reason.trim());
    }
  };

  const handleReasonChange = (event) => {
    setReason(event.target.value);
  };

  // 코어타임 배열을 텍스트로 변환하는 함수
  const coreTimeToText = (coreTimeArray) => {
    if (!coreTimeArray || !Array.isArray(coreTimeArray) || coreTimeArray.length === 0) {
      return '설정된 코어타임이 없습니다';
    }

    const weekdayNames = ['월', '화', '수', '목', '금', '토', '일'];
    const grouped = {};

    // 요일별로 그룹화
    coreTimeArray.forEach(time => {
      const dayName = weekdayNames[time.weekday];
      if (!grouped[dayName]) {
        grouped[dayName] = [];
      }
      
      // JSON 문자열을 파싱하여 label 값 추출
      let startLabel = '';
      let endLabel = '';
      
      try {
        // start가 JSON 문자열인 경우 파싱
        if (typeof time.start === 'string' && time.start.includes('{')) {
          const startData = JSON.parse(time.start);
          startLabel = startData.label || startData.value || time.start;
        } else if (typeof time.start === 'object' && time.start.label) {
          startLabel = time.start.label;
        } else {
          startLabel = time.start;
        }
        
        // end가 JSON 문자열인 경우 파싱
        if (typeof time.end === 'string' && time.end.includes('{')) {
          const endData = JSON.parse(time.end);
          endLabel = endData.label || endData.value || time.end;
        } else if (typeof time.end === 'object' && time.end.label) {
          endLabel = time.end.label;
        } else {
          endLabel = time.end;
        }
      } catch (e) {
        // 파싱 실패시 원본 값 사용
        startLabel = time.start;
        endLabel = time.end;
      }
      
      grouped[dayName].push(`${startLabel} ~ ${endLabel}`);
    });

    // 텍스트로 변환
    return Object.entries(grouped)
      .map(([day, times]) => `${day}: ${times.join(', ')}`)
      .join('\n');
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disablePortal
    >
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack spacing={1}>
          <Typography variant="h6" component="div" gutterBottom>
            ✅ 코어타임 수정 시, 기업에 즉시 공유됩니다.
          </Typography>

          <Box sx={{ pb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              새로운 코어 타임
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                whiteSpace: 'pre-line',
                bgcolor: 'grey.50',
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              {coreTimeToText(coreTime)}
            </Typography>
          </Box>

          <Typography variant="subtitle1">
            1. 변경 전에 반드시 기업과 상의해주시기 바랍니다.
          </Typography>
          <Typography variant="body1">
            코어타임은 계약 체결 전, 기업이 확인했던 항목입니다. 코어타임은 업무 집중 시간으로, 기업이 업무 진행 상황을 판단하는 중요한 기준이니, 신중하게 변경해주세요.
          </Typography>

          <Typography variant="subtitle1">
            2. 변경 사유를 입력해주세요.
          </Typography>

          <TextField
            multiline
            rows={4}
            fullWidth
            value={reason}
            onChange={handleReasonChange}
            placeholder="코어 타임 변경 사유를 입력해주세요"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          size="large"
          sx={{ px: 4 }}
        >
          취소
        </Button>
        <LoadingButton
          onClick={handleConfirm}
          variant="contained"
          size="large"
          loading={submitting}
          disabled={!reason.trim()}
          sx={{ ml: 1, px: 4 }}
        >
          변경하기
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
} 